import { useEffect } from "react";
import { OnboardingType } from "../types";
import { getUnificationParams } from "../utils";
import {
  TOKEN_HANDSHAKE_TIMEOUT_MS,
  getCryptoBlockReason,
  redirectToAllariaDigital,
  reportEmbedContext,
} from "../utils/crypto";

/**
 * The crypto onboarding only works embedded in the Allaria app, which passes the
 * account in the URL and injects the session token via postMessage. Without any
 * of that it renders but can never be completed, so the user is sent to Allaria
 * Digital instead of being left on a form whose button never enables.
 *
 * Only for the main onboarding: the remote QR steps (/dni, /identidad) run
 * top-level on the phone, with no account params, and get their token from
 * Firestore.
 */
const useCryptoGuard = ({
  onboardingType,
  token,
}: {
  onboardingType: OnboardingType;
  token: string;
}) => {
  // Kept apart from the guard so it is recorded once, not again when the token
  // lands.
  useEffect(() => {
    if (onboardingType === "DIGITAL") reportEmbedContext();
  }, [onboardingType]);

  useEffect(() => {
    if (onboardingType !== "DIGITAL") return;

    const reason = getCryptoBlockReason(getUnificationParams());
    if (reason) {
      redirectToAllariaDigital(reason);
      return;
    }

    // The token arrives asynchronously (DIGITAL_ONBOARDING_READY →
    // DIGITAL_ONBOARDING_INIT), so it can only be considered missing after giving
    // the parent time to answer. Once it lands this effect re-runs and the
    // cleanup cancels the timer.
    if (token) return;

    const timeout = setTimeout(
      () => redirectToAllariaDigital("MISSING_TOKEN"),
      TOKEN_HANDSHAKE_TIMEOUT_MS,
    );

    return () => clearTimeout(timeout);
  }, [onboardingType, token]);
};

export default useCryptoGuard;
